/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AgentComparisonResponse } from '../models/AgentComparisonResponse';
import type { AgentTrendResponse } from '../models/AgentTrendResponse';
import type { EventAnalyticsResponse } from '../models/EventAnalyticsResponse';
import type { PhoneCallAnalyticsResponse } from '../models/PhoneCallAnalyticsResponse';
import type { PhoneCallDensityResponse } from '../models/PhoneCallDensityResponse';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class AdminAnalyticsService {
    /**
     * Get Event Analytics
     * Bucketed counts over the ``platform_events`` log plus period totals.
     *
     * Buckets are (time bucket × event type × payload outcome) counts; the
     * totals block includes the pickup rate — answered / all ``call.ended``
     * events in the period (0 when there were no calls).
     * @param dateFrom Only events at/after this instant (ISO datetime).
     * @param dateTo Only events before this instant (ISO datetime, exclusive).
     * @param campaignId Restrict to a single campaign (org-owned).
     * @param eventTypes Repeatable event-type filter; defaults to the call events (call.ended, voicemail.detected).
     * @param granularity Time-bucket size: day | hour.
     * @returns EventAnalyticsResponse Successful Response
     * @throws ApiError
     */
    public static getEventAnalyticsApiAdminAnalyticsEventsGet(
        dateFrom: string,
        dateTo: string,
        campaignId?: (string | null),
        eventTypes?: Array<string>,
        granularity: string = 'day',
    ): CancelablePromise<EventAnalyticsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/admin/analytics/events',
            query: {
                'date_from': dateFrom,
                'date_to': dateTo,
                'campaign_id': campaignId,
                'event_types': eventTypes,
                'granularity': granularity,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Phone Call Density
     * Binned overview of completed calls (time × duration × status).
     *
     * This is the default dashboard query: it returns a few hundred aggregated
     * cells regardless of call volume, so the response stays small and cheap to
     * serialize. Use ``/phone-calls`` to drill a single bin into raw points.
     * @param campaignId Restrict to a single campaign (org-owned).
     * @param startedFrom Only calls started at/after this instant.
     * @param startedTo Only calls started before this instant.
     * @param granularity Time-bucket size: hour | day | week | month. Auto-chosen from the date span when omitted.
     * @returns PhoneCallDensityResponse Successful Response
     * @throws ApiError
     */
    public static getPhoneCallDensityApiAdminAnalyticsPhoneCallsDensityGet(
        campaignId?: (string | null),
        startedFrom?: (string | null),
        startedTo?: (string | null),
        granularity?: (string | null),
    ): CancelablePromise<PhoneCallDensityResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/admin/analytics/phone-calls/density',
            query: {
                'campaign_id': campaignId,
                'started_from': startedFrom,
                'started_to': startedTo,
                'granularity': granularity,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Phone Call Analytics
     * Raw scatter points for completed phone calls in the caller's org.
     *
     * Each point is one call: ``started_at`` (time axis), ``duration_seconds``
     * (magnitude axis) and ``call_status`` (colour: connected / no_answer /
     * voicemail). Intended for drilling into a single density bin or a narrow
     * window — pass the bin's time/duration/status bounds to keep it bounded.
     * @param campaignId Restrict to a single campaign (org-owned).
     * @param startedFrom Only calls started at/after this instant.
     * @param startedTo Only calls started before this instant.
     * @param durationMin Only calls at/above this duration (seconds).
     * @param durationMax Only calls below this duration (seconds).
     * @param callStatus Only this status (connected / no_answer / voicemail).
     * @param limit
     * @returns PhoneCallAnalyticsResponse Successful Response
     * @throws ApiError
     */
    public static getPhoneCallAnalyticsApiAdminAnalyticsPhoneCallsGet(
        campaignId?: (string | null),
        startedFrom?: (string | null),
        startedTo?: (string | null),
        durationMin?: (number | null),
        durationMax?: (number | null),
        callStatus?: (string | null),
        limit: number = 5000,
    ): CancelablePromise<PhoneCallAnalyticsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/admin/analytics/phone-calls',
            query: {
                'campaign_id': campaignId,
                'started_from': startedFrom,
                'started_to': startedTo,
                'duration_min': durationMin,
                'duration_max': durationMax,
                'call_status': callStatus,
                'limit': limit,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Agent Comparison
     * Per-caller-agent rollup for one campaign's A/B test.
     *
     * One row per agent that placed a call: total calls, pickup rate (line
     * answered — connected or voicemail), user_reply conversion rate (a human
     * actually spoke), Wilson 95% confidence intervals + a sample-size flag on
     * those rates, average duration, and the distribution of the agent's contacts
     * across their latest contact state — tagged with the variant ``label``. The
     * owner reads this to pick a winner, then re-weights the variants by hand
     * (there is no automatic promotion in the MVP).
     * @param campaignId Campaign to compare caller agents within (org-owned).
     * @param startedFrom Only calls started at/after this instant.
     * @param startedTo Only calls started before this instant.
     * @returns AgentComparisonResponse Successful Response
     * @throws ApiError
     */
    public static getAgentComparisonApiAdminAnalyticsAgentComparisonGet(
        campaignId: string,
        startedFrom?: (string | null),
        startedTo?: (string | null),
    ): CancelablePromise<AgentComparisonResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/admin/analytics/agent-comparison',
            query: {
                'campaign_id': campaignId,
                'started_from': startedFrom,
                'started_to': startedTo,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Agent Comparison Trend
     * Per-agent time series backing the A/B comparison trend chart.
     *
     * One point per (time bucket × agent): total calls plus the bucket's pickup
     * and user_reply rates, tagged with the variant ``label``. Buckets use the
     * same ``date_trunc`` granularity as the density overview, auto-chosen from
     * the date span when not given.
     * @param campaignId Campaign to compare caller agents within (org-owned).
     * @param startedFrom Only calls started at/after this instant.
     * @param startedTo Only calls started before this instant.
     * @param granularity Time-bucket size: hour | day | week | month. Auto-chosen from the date span when omitted.
     * @returns AgentTrendResponse Successful Response
     * @throws ApiError
     */
    public static getAgentComparisonTrendApiAdminAnalyticsAgentComparisonTrendGet(
        campaignId: string,
        startedFrom?: (string | null),
        startedTo?: (string | null),
        granularity?: (string | null),
    ): CancelablePromise<AgentTrendResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/admin/analytics/agent-comparison/trend',
            query: {
                'campaign_id': campaignId,
                'started_from': startedFrom,
                'started_to': startedTo,
                'granularity': granularity,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Event Analytics
     * Bucketed counts over the ``platform_events`` log plus period totals.
     *
     * Buckets are (time bucket × event type × payload outcome) counts; the
     * totals block includes the pickup rate — answered / all ``call.ended``
     * events in the period (0 when there were no calls).
     * @param dateFrom Only events at/after this instant (ISO datetime).
     * @param dateTo Only events before this instant (ISO datetime, exclusive).
     * @param campaignId Restrict to a single campaign (org-owned).
     * @param eventTypes Repeatable event-type filter; defaults to the call events (call.ended, voicemail.detected).
     * @param granularity Time-bucket size: day | hour.
     * @returns EventAnalyticsResponse Successful Response
     * @throws ApiError
     */
    public static getEventAnalyticsAdminAnalyticsEventsGet(
        dateFrom: string,
        dateTo: string,
        campaignId?: (string | null),
        eventTypes?: Array<string>,
        granularity: string = 'day',
    ): CancelablePromise<EventAnalyticsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/admin/analytics/events',
            query: {
                'date_from': dateFrom,
                'date_to': dateTo,
                'campaign_id': campaignId,
                'event_types': eventTypes,
                'granularity': granularity,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Phone Call Density
     * Binned overview of completed calls (time × duration × status).
     *
     * This is the default dashboard query: it returns a few hundred aggregated
     * cells regardless of call volume, so the response stays small and cheap to
     * serialize. Use ``/phone-calls`` to drill a single bin into raw points.
     * @param campaignId Restrict to a single campaign (org-owned).
     * @param startedFrom Only calls started at/after this instant.
     * @param startedTo Only calls started before this instant.
     * @param granularity Time-bucket size: hour | day | week | month. Auto-chosen from the date span when omitted.
     * @returns PhoneCallDensityResponse Successful Response
     * @throws ApiError
     */
    public static getPhoneCallDensityAdminAnalyticsPhoneCallsDensityGet(
        campaignId?: (string | null),
        startedFrom?: (string | null),
        startedTo?: (string | null),
        granularity?: (string | null),
    ): CancelablePromise<PhoneCallDensityResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/admin/analytics/phone-calls/density',
            query: {
                'campaign_id': campaignId,
                'started_from': startedFrom,
                'started_to': startedTo,
                'granularity': granularity,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Phone Call Analytics
     * Raw scatter points for completed phone calls in the caller's org.
     *
     * Each point is one call: ``started_at`` (time axis), ``duration_seconds``
     * (magnitude axis) and ``call_status`` (colour: connected / no_answer /
     * voicemail). Intended for drilling into a single density bin or a narrow
     * window — pass the bin's time/duration/status bounds to keep it bounded.
     * @param campaignId Restrict to a single campaign (org-owned).
     * @param startedFrom Only calls started at/after this instant.
     * @param startedTo Only calls started before this instant.
     * @param durationMin Only calls at/above this duration (seconds).
     * @param durationMax Only calls below this duration (seconds).
     * @param callStatus Only this status (connected / no_answer / voicemail).
     * @param limit
     * @returns PhoneCallAnalyticsResponse Successful Response
     * @throws ApiError
     */
    public static getPhoneCallAnalyticsAdminAnalyticsPhoneCallsGet(
        campaignId?: (string | null),
        startedFrom?: (string | null),
        startedTo?: (string | null),
        durationMin?: (number | null),
        durationMax?: (number | null),
        callStatus?: (string | null),
        limit: number = 5000,
    ): CancelablePromise<PhoneCallAnalyticsResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/admin/analytics/phone-calls',
            query: {
                'campaign_id': campaignId,
                'started_from': startedFrom,
                'started_to': startedTo,
                'duration_min': durationMin,
                'duration_max': durationMax,
                'call_status': callStatus,
                'limit': limit,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Agent Comparison
     * Per-caller-agent rollup for one campaign's A/B test.
     *
     * One row per agent that placed a call: total calls, pickup rate (line
     * answered — connected or voicemail), user_reply conversion rate (a human
     * actually spoke), Wilson 95% confidence intervals + a sample-size flag on
     * those rates, average duration, and the distribution of the agent's contacts
     * across their latest contact state — tagged with the variant ``label``. The
     * owner reads this to pick a winner, then re-weights the variants by hand
     * (there is no automatic promotion in the MVP).
     * @param campaignId Campaign to compare caller agents within (org-owned).
     * @param startedFrom Only calls started at/after this instant.
     * @param startedTo Only calls started before this instant.
     * @returns AgentComparisonResponse Successful Response
     * @throws ApiError
     */
    public static getAgentComparisonAdminAnalyticsAgentComparisonGet(
        campaignId: string,
        startedFrom?: (string | null),
        startedTo?: (string | null),
    ): CancelablePromise<AgentComparisonResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/admin/analytics/agent-comparison',
            query: {
                'campaign_id': campaignId,
                'started_from': startedFrom,
                'started_to': startedTo,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
    /**
     * Get Agent Comparison Trend
     * Per-agent time series backing the A/B comparison trend chart.
     *
     * One point per (time bucket × agent): total calls plus the bucket's pickup
     * and user_reply rates, tagged with the variant ``label``. Buckets use the
     * same ``date_trunc`` granularity as the density overview, auto-chosen from
     * the date span when not given.
     * @param campaignId Campaign to compare caller agents within (org-owned).
     * @param startedFrom Only calls started at/after this instant.
     * @param startedTo Only calls started before this instant.
     * @param granularity Time-bucket size: hour | day | week | month. Auto-chosen from the date span when omitted.
     * @returns AgentTrendResponse Successful Response
     * @throws ApiError
     */
    public static getAgentComparisonTrendAdminAnalyticsAgentComparisonTrendGet(
        campaignId: string,
        startedFrom?: (string | null),
        startedTo?: (string | null),
        granularity?: (string | null),
    ): CancelablePromise<AgentTrendResponse> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/admin/analytics/agent-comparison/trend',
            query: {
                'campaign_id': campaignId,
                'started_from': startedFrom,
                'started_to': startedTo,
                'granularity': granularity,
            },
            errors: {
                422: `Validation Error`,
            },
        });
    }
}
